import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Film, Headphones, Link2, Loader2, Pencil, Trash2, Upload, Vault, ExternalLink, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { ernaehrungAPI } from '@/lib/api';

/* Tresor (E8 des Ernährungscoachings, 15.10.2026): Dokumente, Videos, Audio und Links vom Coach — je Mitglied
   (Ernährung › Mitglied, `leadId`) oder für alle Coaching-Kunden (Ernährung › Einstellungen, `leadId` null).
   Das Mitglied sieht in der App eigene UND globale Einträge, ab `sichtbar_ab`. Dateien liegen verschlüsselt und
   kommen nur mit Anmeldung als Blob — nie über eine öffentliche Adresse. Geprüft wird auf dem Server (Typ am
   Dateikopf, Größe je Typ); hier steht nur die Bedienung. */

const TYP = {
  dokument: { label: 'Dokument', hinweis: 'PDF, JPG oder PNG', accept: 'application/pdf,image/jpeg,image/png', Icon: FileText },
  video: { label: 'Video', hinweis: 'MP4 oder MOV', accept: 'video/mp4,video/quicktime,.mp4,.mov', Icon: Film },
  audio: { label: 'Audio', hinweis: 'M4A oder MP3', accept: 'audio/mp4,audio/x-m4a,audio/mpeg,.m4a,.mp3', Icon: Headphones },
  link: { label: 'Link', hinweis: 'nur https', accept: '', Icon: Link2 },
};
const groesse = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : `${Math.round(b / 1024)} KB`);
const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const zeit = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return ''; } };
const heuteIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const LEER = { typ: 'dokument', titel: '', beschreibung: '', sichtbar_ab: '', url: '' };

function NeuForm({ leadId, grenzen, onFertig, onAbbruch }) {
  const [f, setF] = useState(LEER);
  const [datei, setDatei] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const dateiRef = useRef(null);
  const istLink = f.typ === 'link';
  const grenze = grenzen?.[f.typ];
  const setze = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const absenden = async (e) => {
    e.preventDefault();
    if (!istLink && !datei) { toast.error('Bitte eine Datei wählen'); return; }
    if (!istLink && grenze && datei.size > grenze * 1024 * 1024) { toast.error(`Die Datei ist zu groß (höchstens ${grenze} MB für ${TYP[f.typ].label})`); return; }
    setLaeuft(true);
    try {
      const felder = { titel: f.titel.trim(), beschreibung: f.beschreibung.trim() || null, sichtbar_ab: f.sichtbar_ab || null };
      if (istLink) await ernaehrungAPI.tresorLink(leadId, { ...felder, url: f.url.trim() });
      else await ernaehrungAPI.tresorHochladen(leadId, datei, { ...felder, typ: f.typ });
      toast.success(istLink ? 'Link abgelegt' : `${TYP[f.typ].label} abgelegt`);
      setF(LEER); setDatei(null); if (dateiRef.current) dateiRef.current.value = '';
      onFertig();
    } catch (err) { toast.error(fehlertext(err, 'Ablegen fehlgeschlagen')); } finally { setLaeuft(false); }
  };
  return (
    <form onSubmit={absenden} className="space-y-2 rounded-md border p-3" aria-label="Neuer Tresor-Eintrag">
      <div className="grid grid-cols-[8rem_1fr] gap-2">
        <FormField label="Art">
          <NativeSelect value={f.typ} onChange={(typ) => { setF((x) => ({ ...x, typ })); setDatei(null); if (dateiRef.current) dateiRef.current.value = ''; }}
            options={Object.entries(TYP).map(([value, t]) => ({ value, label: t.label }))} />
        </FormField>
        <FormField label="Titel"><Input required maxLength={120} value={f.titel} onChange={setze('titel')} placeholder="z. B. Ernährungsplan Woche 1" /></FormField>
      </div>
      {istLink ? (
        <FormField label="Adresse (https)"><Input required type="url" maxLength={1000} value={f.url} onChange={setze('url')} placeholder="https://…" /></FormField>
      ) : (
        <FormField label={`Datei (${TYP[f.typ].hinweis}${grenze ? `, höchstens ${grenze} MB` : ''})`}>
          <input ref={dateiRef} type="file" accept={TYP[f.typ].accept} className="block w-full text-sm file:mr-2 file:rounded-md file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-sm"
            onChange={(e) => { const d = e.target.files?.[0] || null; setDatei(d); if (d && !f.titel) setF((x) => ({ ...x, titel: d.name.replace(/\.[^.]+$/, '').slice(0, 120) })); }} />
        </FormField>
      )}
      <FormField label="Beschreibung (optional)"><Textarea rows={2} maxLength={1000} value={f.beschreibung} onChange={setze('beschreibung')} /></FormField>
      <FormField label="Sichtbar ab (leer = sofort)" hint="Das Mitglied sieht den Eintrag in der App erst ab diesem Tag.">
        <Input type="date" min={heuteIso()} value={f.sichtbar_ab} onChange={setze('sichtbar_ab')} className="w-44" />
      </FormField>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={laeuft || !f.titel.trim()}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Ablegen</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onAbbruch} disabled={laeuft}>Abbrechen</Button>
      </div>
    </form>
  );
}

function AendernForm({ leadId, e, onFertig, onAbbruch }) {
  const [f, setF] = useState({ titel: e.titel || '', beschreibung: e.beschreibung || '', sichtbar_ab: e.sichtbar_ab || '', url: e.url || '' });
  const [laeuft, setLaeuft] = useState(false);
  const setze = (k) => (ev) => setF((x) => ({ ...x, [k]: ev.target.value }));
  const speichern = async (ev) => {
    ev.preventDefault();
    setLaeuft(true);
    try {
      const daten = { titel: f.titel.trim(), beschreibung: f.beschreibung.trim() || null, sichtbar_ab: f.sichtbar_ab || null };
      if (e.typ === 'link') daten.url = f.url.trim();
      await ernaehrungAPI.tresorAendern(leadId, e.id, daten);
      toast.success('Gespeichert'); onFertig();
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <form onSubmit={speichern} className="space-y-2 py-1" aria-label={`${e.titel} bearbeiten`}>
      <FormField label="Titel"><Input required maxLength={120} value={f.titel} onChange={setze('titel')} /></FormField>
      {e.typ === 'link' && <FormField label="Adresse (https)"><Input required type="url" maxLength={1000} value={f.url} onChange={setze('url')} /></FormField>}
      <FormField label="Beschreibung"><Textarea rows={2} maxLength={1000} value={f.beschreibung} onChange={setze('beschreibung')} /></FormField>
      <FormField label="Sichtbar ab (leer = sofort)"><Input type="date" value={f.sichtbar_ab} onChange={setze('sichtbar_ab')} className="w-44" /></FormField>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={laeuft || !f.titel.trim()}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onAbbruch} disabled={laeuft}>Abbrechen</Button>
      </div>
    </form>
  );
}

function Eintrag({ leadId, e, darf, heute, onNeu }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [oeffnet, setOeffnet] = useState(false);
  const { Icon } = TYP[e.typ] || TYP.dokument;
  const nochNicht = e.sichtbar_ab && e.sichtbar_ab > heute;
  const oeffnen = async () => {
    if (e.typ === 'link') { window.open(e.url, '_blank', 'noopener'); return; }
    setOeffnet(true);
    try {
      const r = await ernaehrungAPI.tresorDatei(leadId, e.id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(fehlertext(err, 'Datei konnte nicht geöffnet werden')); } finally { setOeffnet(false); }
  };
  const loeschen = async () => {
    if (!window.confirm(`„${e.titel}“ aus dem Tresor löschen? Die Datei wird entfernt.`)) return;
    try { await ernaehrungAPI.tresorLoeschen(leadId, e.id); toast.success('Gelöscht'); onNeu(); }
    catch (err) { toast.error(fehlertext(err, 'Löschen fehlgeschlagen')); }
  };
  return (
    <li className="px-3 py-2 text-sm">
      {bearbeiten ? <AendernForm leadId={leadId} e={e} onFertig={() => { setBearbeiten(false); onNeu(); }} onAbbruch={() => setBearbeiten(false)} /> : (
        <div className="flex items-start gap-2">
          <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" className="font-medium text-left hover:underline break-words" onClick={oeffnen} disabled={oeffnet}>{e.titel}</button>
              <Badge variant="outline" className="text-[10px]">{TYP[e.typ]?.label || e.typ}</Badge>
              {e.quelle === 'automatik' && <Badge variant="outline" className="text-[10px]">Automatik</Badge>}
              {nochNicht && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-500/10 text-amber-800"><EyeOff className="w-3 h-3 mr-1" />ab {tag(e.sichtbar_ab)}</Badge>}
            </div>
            {e.beschreibung && <p className="text-xs text-muted-foreground whitespace-pre-line break-words">{e.beschreibung}</p>}
            <p className="text-xs text-muted-foreground truncate">
              {zeit(e.hochgeladen_am)}{e.datei ? ` · ${e.datei.name} · ${groesse(e.datei.bytes)}` : e.url ? ` · ${e.url}` : ''}{e.hochgeladen_von ? ` · ${e.hochgeladen_von}` : ''}
            </p>
          </div>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={oeffnen} disabled={oeffnet} title="Öffnen" aria-label={`${e.titel} öffnen`}>{oeffnet ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}</Button>
          {darf && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setBearbeiten(true)} title="Bearbeiten" aria-label={`${e.titel} bearbeiten`}><Pencil className="w-3.5 h-3.5" /></Button>}
          {darf && <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={loeschen} title="Löschen" aria-label={`${e.titel} löschen`}><Trash2 className="w-3.5 h-3.5" /></Button>}
        </div>
      )}
    </li>
  );
}

export function ErnaehrungTresor({ leadId = null, titel }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [neu, setNeu] = useState(false);
  const global = !leadId;
  const laden = useCallback(async () => {
    try { setD((await ernaehrungAPI.tresor(leadId)).data); setFehler(''); }
    catch (e) { setFehler(fehlertext(e, 'Tresor konnte nicht geladen werden')); }
  }, [leadId]);
  useEffect(() => { laden(); }, [laden]);
  const heute = heuteIso();
  const eintraege = d?.eintraege || [];
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Vault className="w-4 h-4" aria-hidden="true" />{titel || (global ? 'Tresor (für alle)' : 'Tresor')}
          {eintraege.length > 0 && <Badge variant="outline">{eintraege.length}</Badge>}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {global ? 'Dokumente, Videos, Audio und Links, die ALLE Mitglieder mit Ernährungscoaching in der App unter „Tresor“ sehen.'
            : 'Dokumente, Videos, Audio und Links nur für dieses Mitglied — in der App unter „Tresor“, zusammen mit den Einträgen für alle.'}
        </p>
        {fehler && !d && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
        {d && !eintraege.length && !neu && <p className="text-sm text-muted-foreground">Noch nichts abgelegt.</p>}
        {eintraege.length > 0 && (
          <ul className="divide-y rounded-md border">
            {eintraege.map((e) => <Eintrag key={e.id} leadId={leadId} e={e} darf={!!d.darf_bearbeiten} heute={heute} onNeu={laden} />)}
          </ul>
        )}
        {d?.darf_bearbeiten && (neu
          ? <NeuForm leadId={leadId} grenzen={d.grenzen_mb} onFertig={() => { setNeu(false); laden(); }} onAbbruch={() => setNeu(false)} />
          : <Button size="sm" variant="outline" onClick={() => setNeu(true)}><Upload className="w-4 h-4 mr-1" />Ablegen</Button>)}
        {d && !d.darf_bearbeiten && <p className="text-xs text-muted-foreground">Ablegen und Löschen braucht das Recht „Ernährung bearbeiten“.</p>}
      </CardContent>
    </Card>
  );
}

export default ErnaehrungTresor;
